import { useState, useEffect, useCallback, useRef } from "react";
import { createPortal } from "react-dom";
import api from "../../api/api";
import "./Etiqueta.scss";

// ─── Constantes ───────────────────────────────────────────────────────────────

const CATEGORIAS_ETIQUETA = [
  {
    key: "produto",
    label: "Etiqueta de Produto",
    desc: "Usada para identificação no estoque e prateleiras",
    icon: "ti-package",
    colorBg:     "var(--color-success-bg)",
    colorText:   "var(--color-success)",
    colorBorder: "var(--color-success-border)",
  },
];

// ─── Service ──────────────────────────────────────────────────────────────────

const svc = {
  getAll:    (cid)         => api.get(`/clientes/${cid}/templates`),
  create:    (cid, p)      => api.post(`/clientes/${cid}/templates`, p),
  update:    (cid, tid, p) => api.put(`/clientes/${cid}/templates/${tid}`, p),
  remove:    (cid, tid)    => api.delete(`/clientes/${cid}/templates/${tid}`),
  restaurar: (cid, tid)    => api.post(`/clientes/${cid}/templates/${tid}/restaurar`),
  setPadrao: (cid, tid)    => api.post(`/clientes/${cid}/templates/${tid}/padrao`),
};

// ─── Hook ─────────────────────────────────────────────────────────────────────

function useTemplates(clienteId) {
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [erro, setErro]       = useState(null);

  const fetchData = useCallback(async () => {
    if (!clienteId) return;
    setLoading(true);
    setErro(null);
    try {
      const { data: res } = await svc.getAll(clienteId);
      setData(res);
    } catch (e) {
      setErro(e.response?.data?.error ?? "Erro ao carregar templates");
    } finally {
      setLoading(false);
    }
  }, [clienteId]);

  useEffect(() => { fetchData(); }, [fetchData]);
  return { data, loading, erro, refetch: fetchData };
}

// ─── useEscapeClose ───────────────────────────────────────────────────────────

function useEscapeClose(onClose) {
  useEffect(() => {
    const h = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [onClose]);
}

// ─── Barcode ──────────────────────────────────────────────────────────────────

function Barcode({ height = 18 }) {
  const bars = [3,1,2,1,3,2,1,2,3,1,2,1,2,3,1,2,1,3,2,1,2,1,3,2,3,1,2,1];
  return (
    <div style={{ display: "flex", gap: 1 }}>
      {bars.map((w, i) => (
        <div key={i} style={{
          width: w, height,
          background: "#111",
          borderRadius: 0.5,
        }} />
      ))}
    </div>
  );
}

// ─── EtiquetaMock ─────────────────────────────────────────────────────────────

function EtiquetaMock({ categ }) {
  const isVenda  = categ === "venda";
  const isRecibo = categ === "bobina_80mm" || categ === "a4";

  if (isRecibo) {
    return (
      <div className="te__etiqueta-mock" style={{ gap: 3 }}>
        <div className="te__etiqueta-mock-name" style={{ fontSize: 9 }}>LOJA EXEMPLO LTDA</div>
        <div className="te__etiqueta-mock-sub">CNPJ: 00.000.000/0001-00</div>
        <div className="te__etiqueta-mock-sub" style={{ borderTop: "0.5px solid #ccc", paddingTop: 3, marginTop: 1 }}>CUPOM #042</div>
        <div className="te__etiqueta-mock-sub">Camiseta Básica  1  R$89,90</div>
        <div className="te__etiqueta-mock-sub">Calça Slim       2  R$79,90</div>
        <div style={{ borderTop: "0.5px solid #ccc", paddingTop: 3, marginTop: 1, fontSize: 9, fontWeight: 600 }}>TOTAL: R$ 249,70</div>
        <div className="te__etiqueta-mock-sub">Pix · Troco: R$ 0,30</div>
      </div>
    );
  }

  return (
    <div className="te__etiqueta-mock">
      <div className="te__etiqueta-mock-name">{isVenda ? "PRODUTO EXEMPLO" : "CAMISETA BÁSICA M"}</div>
      <div className="te__etiqueta-mock-sub">{isVenda ? "VD-00234 · Venda #0042" : "SKU: EST-0042"}</div>
      <Barcode height={14} />
      <div className="te__etiqueta-mock-price">{isVenda ? "R$ 89,90" : "R$ 79,90"}</div>
    </div>
  );
}

// ─── PreviewFull ──────────────────────────────────────────────────────────────

function PreviewFull({ categ }) {
  const isVenda = categ === "venda";
  return (
    <div className="te__preview-full">
      <p className="te__preview-full-name">{isVenda ? "CAMISETA BÁSICA ALGODÃO - P" : "CALÇA JEANS SLIM - 38"}</p>
      <p className="te__preview-full-sub">{isVenda ? "VD-00234 · Cliente: João Silva" : "SKU: EST-0042 · Estoque: 12 un"}</p>
      <Barcode height={28} />
      <p className="te__preview-full-price">R$ 89,90</p>
      <p className="te__preview-full-date">{isVenda ? "10/01/2025 · Vendedor: Ana" : "Atualizado 10/01/2025"}</p>
    </div>
  );
}

// ─── FloatingPanel ────────────────────────────────────────────────────────────

function FloatingPanel({
  open,
  onClose,
  titulo,
  sub,
  footer,
  children,
}) {
  useEscapeClose(onClose);

  if (!open) return null;

  return createPortal(
    <div
      className="te__modal-overlay"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        className="te__modal-window"
        role="dialog"
        aria-modal="true"
      >
        <div className="te__modal-header">
          <div>
            <h2 className="te__modal-title">{titulo}</h2>

            {sub && (
              <p className="te__modal-sub">
                {sub}
              </p>
            )}
          </div>

          <button
            className="te__btn te__btn--icon"
            onClick={onClose}
          >
            <i className="ti ti-x" />
          </button>
        </div>

        <div className="te__modal-content">
          {children}
        </div>

        {footer && (
          <div className="te__modal-footer">
            {footer}
          </div>
        )}
      </div>
    </div>,
    document.body
  );
}

// ─── PanelPreview ─────────────────────────────────────────────────────────────

function PanelPreview({ template: t, categ, sistema, onClose, onUsarBase, onXml }) {
  const categInfo = CATEGORIAS_ETIQUETA.find((c) => c.key === categ);

  const footer = (
    <>
      <button className="te__btn" onClick={onXml}>
        <i className="ti ti-code" style={{ fontSize: 12 }} /> Ver XML
      </button>
      {sistema ? (
        <button className="te__btn te__btn--primary" onClick={() => { onClose(); onUsarBase(); }}>
          <i className="ti ti-copy" style={{ fontSize: 12 }} /> Usar como base
        </button>
      ) : (
        <button className="te__btn te__btn--primary" onClick={onClose}>Fechar</button>
      )}
    </>
  );

  return (
    <FloatingPanel
      open
      onClose={onClose}
      titulo={t.nome}
      sub={`${categInfo?.label ?? categ}${sistema ? " · Modelo do sistema" : ""}`}
      footer={footer}
    >
      <p style={{ fontSize: 12, color: "var(--color-text-secondary)", marginBottom: 12 }}>
        Preview com dados de exemplo
      </p>
      <PreviewFull categ={categ} />
      <div className="te__info-box" style={{ marginTop: 14 }}>
        <i className="ti ti-info-circle" style={{ fontSize: 14, flexShrink: 0 }} />
        O preview é ilustrativo. A aparência final depende do XML configurado e da impressora utilizada.
      </div>
    </FloatingPanel>
  );
}

// ─── PanelXml ─────────────────────────────────────────────────────────────────

function PanelXml({ template: t, onClose }) {
  const [copiado, setCopiado] = useState(false);

  const copiar = () => {
    navigator.clipboard.writeText(t.conteudo_xml).then(() => {
      setCopiado(true);
      setTimeout(() => setCopiado(false), 2000);
    });
  };

  const footer = (
    <>
      <button className={`te__btn${copiado ? " te__btn--primary" : ""}`} onClick={copiar}>
        <i className={`ti ${copiado ? "ti-check" : "ti-copy"}`} style={{ fontSize: 12 }} />
        {copiado ? "Copiado!" : "Copiar XML"}
      </button>
      <button className="te__btn" onClick={onClose}>Fechar</button>
    </>
  );

  return (
    <FloatingPanel
      open
      onClose={onClose}
      titulo={`XML — ${t.nome}`}
      sub={t.is_system ? "Modelo do sistema · somente leitura" : undefined}
      footer={footer}
    >
      <pre className="te__xml-pre">{t.conteudo_xml}</pre>
    </FloatingPanel>
  );
}

// ─── PanelForm ────────────────────────────────────────────────────────────────

function PanelForm({ titulo, categoriaFixa, tipoFixo, nomeInicial, xmlInicial, variaveis, saving, onClose, onSave }) {
  const [nome, setNome] = useState(nomeInicial || "");
  const [xml, setXml]   = useState(xmlInicial  || "");
  const xmlRef = useRef(null);

  const categ     = categoriaFixa;
  const categInfo = CATEGORIAS_ETIQUETA.find((c) => c.key === categ);
  const podeSalvar = nome.trim() && xml.trim() && categ;

  const inserirVar = (v) => {
    const el = xmlRef.current;
    if (!el) { setXml((x) => x + v); return; }
    const start = el.selectionStart;
    const end   = el.selectionEnd;
    setXml(xml.slice(0, start) + v + xml.slice(end));
    requestAnimationFrame(() => {
      el.selectionStart = el.selectionEnd = start + v.length;
      el.focus();
    });
  };

  const footer = (
    <>
      <button className="te__btn" onClick={onClose}>Cancelar</button>
      <button
        className="te__btn te__btn--primary"
        onClick={() => onSave({ nome, xml, categoria: categ, tipo: tipoFixo })}
        disabled={saving || !podeSalvar}
      >
        {saving ? "Salvando…" : "Salvar template"}
      </button>
    </>
  );

  return (
    <FloatingPanel
      open
      onClose={onClose}
      titulo={titulo}
      sub={categInfo?.label}
      footer={footer}
    >
      {categInfo && (
        <div>
          <label className="te__label">Categoria</label>
          <div className="te__categ-pill" style={{ background: categInfo.colorBg, borderColor: categInfo.colorBorder }}>
            <i className={`ti ${categInfo.icon}`} style={{ fontSize: 14, color: categInfo.colorText }} />
            <span style={{ fontSize: 13, fontWeight: 500, color: categInfo.colorText }}>{categInfo.label}</span>
          </div>
        </div>
      )}

      <div>
        <label className="te__label">Nome do template</label>
        <input
          className="te__input"
          value={nome}
          onChange={(e) => setNome(e.target.value)}
          placeholder={`Ex: Etiqueta ${categInfo?.label ?? "Personalizada"}`}
          autoFocus
        />
      </div>

      {variaveis.length > 0 && (
        <div>
          <p style={{ fontSize: 12, color: "var(--color-text-secondary)", margin: "0 0 7px", fontWeight: 500 }}>
            Variáveis disponíveis — clique para inserir no cursor:
          </p>
          <div className="te__var-chips">
            {variaveis.map((v) => (
              <button key={v} className="te__var-chip" onClick={() => inserirVar(v)}>{v}</button>
            ))}
          </div>
        </div>
      )}

      <div>
        <label className="te__label">Conteúdo XML</label>
        <textarea
          ref={xmlRef}
          className="te__input te__input--mono"
          value={xml}
          onChange={(e) => setXml(e.target.value)}
          rows={18}
          spellCheck={false}
        />
      </div>
    </FloatingPanel>
  );
}

// ─── CardMenu via portal ──────────────────────────────────────────────────────

function CardMenu({ anchorEl, template: t, onClose, onToggle, onRestore, onDelete }) {
  const [pos, setPos] = useState(null);
  const panelRef = useRef(null);

  useEffect(() => {
    if (!anchorEl) return;
    const r = anchorEl.getBoundingClientRect();
    const panelH = panelRef.current?.offsetHeight ?? 220;
    const panelW = 220;
    const spaceBelow = window.innerHeight - r.bottom;
    const openUp = spaceBelow < panelH + 8;
    setPos({
      top: openUp
        ? r.top + window.scrollY - panelH - 4
        : r.bottom + window.scrollY + 4,
      left: Math.min(r.right - panelW, window.innerWidth - panelW - 8),
    });
  }, [anchorEl]);

  useEffect(() => {
    if (!anchorEl) return;
    const h = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [anchorEl, onClose]);

  if (!anchorEl) return null;

  return createPortal(
    <>
      <div style={{ position: "fixed", inset: 0, zIndex: 999 }} onClick={onClose} />
      <div
        ref={panelRef}
        className="te__card-panel"
        style={{ position: "absolute", zIndex: 1000, width: 220, ...pos }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="te__card-panel__header">
          <span className="te__card-panel__title">{t.nome}</span>
          <button className="te__card-panel__close" onClick={onClose} aria-label="Fechar">
            <i className="ti ti-x" style={{ fontSize: 13 }} />
          </button>
        </div>
        <div className="te__card-panel__body">
          <button className="te__card-panel__item" onClick={() => { onClose(); onToggle(t); }}>
            <i className={`ti ${t.ativo ? "ti-eye-off" : "ti-eye"}`} style={{ fontSize: 14 }} />
            <div>
              <p className="te__card-panel__item-label">{t.ativo ? "Desativar template" : "Ativar template"}</p>
              <p className="te__card-panel__item-desc">{t.ativo ? "Deixa de aparecer nas opções" : "Volta a aparecer nas opções"}</p>
            </div>
          </button>
          <button className="te__card-panel__item" onClick={() => { onClose(); onRestore(t.id); }}>
            <i className="ti ti-refresh" style={{ fontSize: 14 }} />
            <div>
              <p className="te__card-panel__item-label">Restaurar XML</p>
              <p className="te__card-panel__item-desc">Volta ao modelo original do sistema</p>
            </div>
          </button>
        </div>
        <div className="te__card-panel__danger-zone">
          <button className="te__card-panel__item te__card-panel__item--danger" onClick={() => { onClose(); onDelete(t.id); }}>
            <i className="ti ti-trash" style={{ fontSize: 14 }} />
            <div>
              <p className="te__card-panel__item-label">Excluir template</p>
              <p className="te__card-panel__item-desc">Ação irreversível</p>
            </div>
          </button>
        </div>
      </div>
    </>,
    document.body,
  );
}

// ─── CardMeu ─────────────────────────────────────────────────────────────────

function CardMeu({ template: t, categ, onPreview, onEdit, onXml, onDelete, onToggle, onRestore, onSetPadrao }) {
  const [menuAnchor, setMenuAnchor] = useState(null);
  const btnRef = useRef(null);
  const isPadrao = t.is_padrao_categoria;

  return (
    <div
      className={`te__card${isPadrao ? " te__card--padrao" : ""}${!t.ativo ? " te__card--inativo" : ""}`}
      onClick={onPreview}
    >
      <div className="te__thumb">
        <EtiquetaMock categ={categ} />
        <div className="te__thumb-overlay">
          <i className="ti ti-eye" style={{ fontSize: 13 }} /> Preview
        </div>
      </div>

      <div className="te__card-badges">
        <div
          className={`te__check${isPadrao ? " te__check--ativo" : ""}`}
          style={{ marginRight: "auto" }}
          title={isPadrao ? "Template padrão" : "Não é o padrão"}
        >
          {isPadrao && <i className="ti ti-check" style={{ fontSize: 10 }} />}
        </div>
        {isPadrao && (
          <span className="te__badge te__badge--warning">
            <i className="ti ti-star-filled" style={{ fontSize: 9 }} /> Padrão
          </span>
        )}
        {!t.ativo && (
          <span className="te__badge te__badge--muted">Inativo</span>
        )}
      </div>

      <p className="te__card-nome">{t.nome}</p>
      <p className="te__card-meta">
        Atualizado {new Date(t.updated_at || t.created_at).toLocaleDateString("pt-BR")}
      </p>

      <div className="te__card-actions" onClick={(e) => e.stopPropagation()}>
        <button className="te__btn" style={{ fontSize: 11 }} onClick={onXml}>
          <i className="ti ti-code" style={{ fontSize: 11 }} /> XML
        </button>
        <button className="te__btn" style={{ fontSize: 11 }} onClick={onEdit}>
          <i className="ti ti-edit" style={{ fontSize: 11 }} /> Editar
        </button>
        {!isPadrao && (
          <button className="te__btn te__btn--warning" style={{ fontSize: 11 }} onClick={() => onSetPadrao(t)}>
            <i className="ti ti-star" style={{ fontSize: 11 }} />
          </button>
        )}
        <button
          ref={btnRef}
          className="te__btn"
          style={{ fontSize: 11, padding: "5px 8px", marginLeft: "auto" }}
          onClick={() => setMenuAnchor((a) => (a ? null : btnRef.current))}
          aria-label="Mais opções"
        >
          <i className="ti ti-dots" style={{ fontSize: 12 }} />
        </button>
        <CardMenu
          anchorEl={menuAnchor}
          template={t}
          onClose={() => setMenuAnchor(null)}
          onToggle={onToggle}
          onRestore={onRestore}
          onDelete={onDelete}
        />
      </div>
    </div>
  );
}

// ─── CardSistema ──────────────────────────────────────────────────────────────

function CardSistema({ template: t, categ, onPreview, onXml, onUsarBase }) {
  return (
    <div className="te__card te__card--sistema" onClick={onPreview}>
      <div className="te__thumb">
        <EtiquetaMock categ={categ} />
        <div className="te__thumb-overlay">
          <i className="ti ti-eye" style={{ fontSize: 13 }} /> Preview
        </div>
      </div>
      <span className="te__badge te__badge--muted" style={{ alignSelf: "flex-start", margin: "8px 10px 0" }}>
        <i className="ti ti-lock" style={{ fontSize: 10 }} /> Sistema
      </span>
      <p className="te__card-nome">{t.nome}</p>
      {t.descricao && <p className="te__card-desc">{t.descricao}</p>}
      <div className="te__card-actions" onClick={(e) => e.stopPropagation()}>
        <button className="te__btn" style={{ fontSize: 11 }} onClick={onXml}>
          <i className="ti ti-code" style={{ fontSize: 11 }} /> XML
        </button>
        <button className="te__btn te__btn--primary" style={{ fontSize: 11 }} onClick={onUsarBase}>
          <i className="ti ti-copy" style={{ fontSize: 11 }} /> Usar como base
        </button>
      </div>
    </div>
  );
}

// ─── CardNovo ─────────────────────────────────────────────────────────────────

function CardNovo({ categKey, onClick }) {
  return (
    <button className="te__card-novo" onClick={onClick} aria-label="Criar novo template">
      <i className="ti ti-plus" style={{ fontSize: 20 }} />
      <span>Nova etiqueta</span>
    </button>
  );
}

// ─── ModalTrocarPadrao ────────────────────────────────────────────────────────

function ModalTrocarPadrao({ lista, categ, onClose, onSetPadrao }) {
  useEscapeClose(onClose);
  return (
    <div className="te__overlay" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="te__modal">
        <div className="te__modal-header">
          <div>
            <h2 className="te__modal-header-title">Trocar template padrão</h2>
            <p className="te__modal-header-sub">{categ?.label} — escolha qual será usado por padrão</p>
          </div>
          <button className="te__btn te__btn--icon" onClick={onClose}>
            <i className="ti ti-x" style={{ fontSize: 14 }} />
          </button>
        </div>
        <div className="te__modal-body">
          <p style={{ fontSize: 13, color: "var(--color-text-secondary)", marginBottom: 14 }}>
            O template padrão é usado automaticamente ao imprimir etiquetas desta categoria sem selecionar um específico.
          </p>
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {lista.map((t) => (
              <div key={t.id} className={`te__padrao-row${t.is_padrao_categoria ? " te__padrao-row--atual" : ""}`}>
                <div className={`te__check${t.is_padrao_categoria ? " te__check--ativo" : ""}`}>
                  {t.is_padrao_categoria && <i className="ti ti-check" style={{ fontSize: 10 }} />}
                </div>
                <div style={{ flex: 1 }}>
                  <p className="te__padrao-row-nome">{t.nome}</p>
                  {!t.ativo && <p className="te__padrao-row-inativo">Inativo</p>}
                </div>
                {t.is_padrao_categoria ? (
                  <span className="te__badge te__badge--warning">
                    <i className="ti ti-star-filled" style={{ fontSize: 9 }} /> Atual
                  </span>
                ) : (
                  <button className="te__btn te__btn--warning" style={{ fontSize: 12 }} onClick={() => onSetPadrao(t)}>
                    <i className="ti ti-star" style={{ fontSize: 11 }} /> Definir como padrão
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>
        <div className="te__modal-footer">
          <button className="te__btn" onClick={onClose}>Fechar</button>
        </div>
      </div>
    </div>
  );
}

// ─── Page principal ───────────────────────────────────────────────────────────

export default function TemplatesEtiquetaPage() {
  const clienteId = JSON.parse(localStorage.getItem("usuario"))?.cliente_id;
  const { data, loading, erro, refetch } = useTemplates(clienteId);

  const [aba, setAba]       = useState("meus");
  const [panel, setPanel]   = useState(null);   // painel flutuante
  const [modal, setModal]   = useState(null);   // modal fullscreen (trocar-padrao)
  const [saving, setSaving] = useState(false);
  const [toast, setToast]   = useState(null);

  const showToast = (msg, tipo = "sucesso") => {
    setToast({ msg, tipo });
    setTimeout(() => setToast(null), 3500);
  };

  const closePanel = () => setPanel(null);

  const handleDelete = async (id) => {
    if (!window.confirm("Remover este template? Esta ação não pode ser desfeita.")) return;
    try {
      await svc.remove(clienteId, id);
      showToast("Template removido");
      refetch();
    } catch (e) {
      showToast(e.response?.data?.error ?? "Erro ao remover", "erro");
    }
  };

  const handleToggleAtivo = async (t) => {
    try {
      await svc.update(clienteId, t.id, { ativo: !t.ativo });
      showToast(t.ativo ? "Template desativado" : "Template ativado");
      refetch();
    } catch {
      showToast("Erro ao atualizar", "erro");
    }
  };

  const handleRestaurar = async (id) => {
    if (!window.confirm("Restaurar o XML do modelo do sistema? Suas alterações serão perdidas.")) return;
    try {
      await svc.restaurar(clienteId, id);
      showToast("Template restaurado");
      refetch();
    } catch (e) {
      showToast(e.response?.data?.error ?? "Erro ao restaurar", "erro");
    }
  };

  const handleSetPadrao = async (t) => {
    try {
      await svc.setPadrao(clienteId, t.id);
      showToast(`"${t.nome}" agora é o padrão de ${t.categoria === "venda" ? "Venda" : "Produto"}`);
      refetch();
    } catch (e) {
      showToast(e.response?.data?.error ?? "Erro ao definir padrão", "erro");
    }
  };

  const handleCriar = async ({ nome, xml, categoria, tipo: tipoOverride }) => {
    setSaving(true);
    try {
      const tipo = tipoOverride ?? "etiqueta_produto";
      await svc.create(clienteId, { tipo, nome, conteudo_xml: xml, categoria });
      showToast("Template criado!");
      closePanel();
      refetch();
    } catch (e) {
      showToast(e.response?.data?.error ?? "Erro ao criar", "erro");
    } finally {
      setSaving(false);
    }
  };

  const handleEditar = async ({ nome, xml }) => {
    setSaving(true);
    try {
      await svc.update(clienteId, panel.template.id, { nome, conteudo_xml: xml });
      showToast("Template salvo!");
      closePanel();
      refetch();
    } catch (e) {
      showToast(e.response?.data?.error ?? "Erro ao salvar", "erro");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <LoadingState />;
  if (erro)    return <ErroState msg={erro} onRetry={refetch} />;

  const meus    = (data?.templates ?? []).filter((t) => t.tipo === "etiqueta_produto");
  const variaveis = data?.variaveis?.etiqueta_produto ?? [];

  const meusPorCateg = Object.fromEntries(
    CATEGORIAS_ETIQUETA.map((c) => [c.key, meus.filter((t) => t.categoria === c.key)])
  );

  return (
    <div className="te">
      {toast && <Toast msg={toast.msg} tipo={toast.tipo} />}

      <div className="te__header">
        <h1 className="te__header-title">Templates de Etiqueta</h1>
        <p className="te__header-desc">Gerencie modelos de etiqueta por categoria e defina qual será usado por padrão</p>
      </div>

      <div className="te__toolbar">
        <div className="te__tab-bar">
          <button className={`te__tab${aba === "meus" ? " te__tab--ativa" : ""}`} onClick={() => setAba("meus")}>
            Meus templates
          </button>
          <button className={`te__tab${aba === "sistema" ? " te__tab--ativa" : ""}`} onClick={() => setAba("sistema")}>
            Modelos do sistema
          </button>
        </div>
        {aba === "meus" && (
          <button className="te__btn te__btn--primary" onClick={() => setPanel({ tipo: "criar" })}>
            <i className="ti ti-plus" style={{ fontSize: 13 }} /> Nova etiqueta
          </button>
        )}
      </div>

      {/* ── Aba Meus ── */}
      {aba === "meus" && (
        <div>
          {CATEGORIAS_ETIQUETA.map((categ) => {
            const lista  = meusPorCateg[categ.key] ?? [];
            const padrao = lista.find((t) => t.is_padrao_categoria);
            return (
              <div key={categ.key} className="te__categ-section">
                <div className="te__categ-header">
                  <div className="te__categ-icon" style={{ background: categ.colorBg, color: categ.colorText }}>
                    <i className={`ti ${categ.icon}`} style={{ fontSize: 16 }} />
                  </div>
                  <div>
                    <p className="te__categ-title">{categ.label}</p>
                    <p className="te__categ-desc">{categ.desc}</p>
                  </div>
                  <span className="te__badge te__badge--muted" style={{ marginLeft: "auto" }}>
                    {lista.length} template{lista.length !== 1 ? "s" : ""}
                  </span>
                </div>

                <div className={`te__padrao-banner${!padrao ? " te__padrao-banner--none" : ""}`}>
                  <i className={`ti ${padrao ? "ti-star-filled" : "ti-star"}`}
                    style={{ fontSize: 16, color: padrao ? "var(--color-text-warning)" : "var(--color-text-tertiary)" }} />
                  <div style={{ flex: 1 }}>
                    <p className="te__padrao-banner-label">Template padrão desta categoria</p>
                    <p className={`te__padrao-banner-nome te__padrao-banner-nome--${padrao ? "ativo" : "vazio"}`}>
                      {padrao?.nome ?? "Nenhum definido"}
                    </p>
                  </div>
                  {lista.length > 1 && (
                    <button className="te__btn" style={{ fontSize: 12 }} onClick={() => setModal({ tipo: "trocar-padrao", categ: categ.key })}>
                      <i className="ti ti-switch-horizontal" style={{ fontSize: 12 }} /> Trocar
                    </button>
                  )}
                </div>

                <div className="te__cards-grid">
                  {lista.map((t) => (
                    <CardMeu
                      key={t.id}
                      template={t}
                      categ={categ.key}
                      onPreview={() => setPanel({ tipo: "preview", template: t, categ: categ.key })}
                      onEdit={() => setPanel({ tipo: "editar", template: t })}
                      onXml={() => setPanel({ tipo: "xml", template: t })}
                      onDelete={handleDelete}
                      onToggle={handleToggleAtivo}
                      onRestore={handleRestaurar}
                      onSetPadrao={handleSetPadrao}
                    />
                  ))}
                  <CardNovo
                    categKey={categ.key}
                    onClick={() => setPanel({ tipo: "criar", categoriaFixa: categ.key })}
                  />
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* ── Aba Sistema ── */}
      {aba === "sistema" && (
        <div>
          <p style={{ fontSize: 13, color: "var(--color-text-secondary)", marginBottom: "1.5rem" }}>
            Modelos prontos — visualize o preview, copie o XML ou use como base para criar o seu.
          </p>
          {CATEGORIAS_ETIQUETA.map((categ) => {
            const lista = (data?.templates_sistema ?? []).filter(
              (t) => t.tipo === "etiqueta_produto" && t.categoria === categ.key
            );
            return (
              <div key={categ.key} className="te__categ-section">
                <div className="te__categ-header">
                  <div className="te__categ-icon" style={{ background: categ.colorBg, color: categ.colorText }}>
                    <i className={`ti ${categ.icon}`} style={{ fontSize: 16 }} />
                  </div>
                  <p className="te__categ-title">{categ.label}</p>
                </div>
                {lista.length === 0 ? (
                  <EmptyState msg="Nenhum modelo disponível nesta categoria" />
                ) : (
                  <div className="te__cards-grid">
                    {lista.map((t) => (
                      <CardSistema
                        key={t.id}
                        template={t}
                        categ={categ.key}
                        onPreview={() => setPanel({ tipo: "preview", template: t, categ: categ.key, sistema: true })}
                        onXml={() => setPanel({ tipo: "xml", template: t })}
                        onUsarBase={() => setPanel({ tipo: "criar", categoriaFixa: categ.key, tipoFixo: "etiqueta_produto", xmlInicial: t.conteudo_xml, nomeInicial: `Cópia de ${t.nome}` })}
                      />
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* ── Painéis flutuantes ── */}
      {panel?.tipo === "preview" && (
        <PanelPreview
          template={panel.template}
          categ={panel.categ}
          sistema={panel.sistema}
          onClose={closePanel}
          onUsarBase={() => setPanel({ tipo: "criar", categoriaFixa: panel.categ, xmlInicial: panel.template.conteudo_xml, nomeInicial: `Cópia de ${panel.template.nome}` })}
          onXml={() => setPanel({ tipo: "xml", template: panel.template })}
        />
      )}
      {panel?.tipo === "xml" && (
        <PanelXml template={panel.template} onClose={closePanel} />
      )}
      {(panel?.tipo === "criar" || panel?.tipo === "editar") && (
        <PanelForm
          titulo={panel.tipo === "criar" ? "Novo etiqueta" : "Editar template"}
          categoriaFixa={panel.categoriaFixa ?? panel.template?.categoria}
          tipoFixo={panel.tipoFixo ?? panel.template?.tipo ?? "etiqueta_produto"}
          nomeInicial={panel.nomeInicial ?? panel.template?.nome ?? ""}
          xmlInicial={panel.xmlInicial ?? panel.template?.conteudo_xml ?? ""}
          variaveis={panel.tipoFixo === "recibo_venda" ? (data?.variaveis?.recibo_venda ?? []) : variaveis}
          saving={saving}
          onClose={closePanel}
          onSave={panel.tipo === "criar" ? handleCriar : handleEditar}
        />
      )}

      {/* ── Modal trocar padrão (mantém como modal fullscreen pois é uma escolha simples) ── */}
      {modal?.tipo === "trocar-padrao" && (
        <ModalTrocarPadrao
          lista={meusPorCateg[modal.categ] ?? []}
          categ={CATEGORIAS_ETIQUETA.find((c) => c.key === modal.categ)}
          onClose={() => setModal(null)}
          onSetPadrao={async (t) => { await handleSetPadrao(t); setModal(null); }}
        />
      )}
    </div>
  );
}

// ─── Utilitários ──────────────────────────────────────────────────────────────

function EmptyState({ msg, action }) {
  return (
    <div className="te__empty">
      <i className="ti ti-file-off te__empty-icon" />
      <p className="te__empty-msg">{msg}</p>
      {action}
    </div>
  );
}

function Toast({ msg, tipo }) {
  return (
    <div className={`te__toast te__toast--${tipo}`}>
      <i className={`ti ${tipo === "erro" ? "ti-alert-circle" : "ti-check"}`} style={{ fontSize: 15 }} />
      {msg}
    </div>
  );
}

function LoadingState() {
  return (
    <div className="te__loading">
      <i className="ti ti-loader-2 te__spinner" />
    </div>
  );
}

function ErroState({ msg, onRetry }) {
  return (
    <div className="te__erro">
      <i className="ti ti-alert-triangle te__erro-icon" />
      <p className="te__erro-msg">{msg}</p>
      <button className="te__btn" onClick={onRetry}>Tentar novamente</button>
    </div>
  );
}